<script setup lang="ts">
import type { LoginParam } from '@/types/user'
import type { FormInstance, FormRules } from 'element-plus'
import { useUserInfoStore } from '@/stores/modules/user'
import { onBeforeUnmount, onMounted, reactive, ref, defineComponent } from 'vue'
import router from '@/router'
import { User, Lock } from '@element-plus/icons-vue'

// 定义图标组件
const icons = {
  User,
  Lock
}

/***************************** 数据源 ******************************************/
// 获取用户信息缓存仓库
const userInfo = useUserInfoStore()

// 数据绑定对象
const loginData = reactive<LoginParam>({
  username: '',
  password: ''
})

// 验证规则
const rules = reactive<FormRules>({
  username: [{ required: true, message: '用户名不能为空', trigger: 'blur' }],
  password: [{ required: true, message: '密码不能为空', trigger: 'blur' }]
})

// 登录动作处理
// 定义表单校验实例 -> 会去自动匹配 template 中 ref="ruleFormRef" 指定的表单元素，拿到指定的对象
const ruleFormRef = ref<FormInstance>()
// 加载动画
const loading = ref(false)

/***************************** 定义方法 ******************************************/
const handleLogin = async () => {
  // 表单验证
  const valid = await ruleFormRef.value?.validate()
  // 校验不成功直接 return
  if (!valid) {
    return false
  }
  // 校验成功进行后续操作
  // 1.加载 loading
  loading.value = true
  // 2.登录请求 获取token
  userInfo
    .login(loginData)
    .then(async () => {
      // 获取用户信息判断角色
      await userInfo.getUserInfo()
      const role = userInfo.user?.roleCode
      const redirect = router.currentRoute.value.query.redirect as string
      if (redirect) {
        router.push(redirect)
      } else if (role === 'admin' || role === 'superadmin') {
        router.push('/admin/dashboard')
      } else {
        router.push('/customer/home')
      }
    })
    .catch(() => {
      // 验证失败，重新加载验证码
    })
    .finally(() => {
      // 验证结束，隐藏loading
      loading.value = false
    })
}

// 监听回车事件
const onKeyUp = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    handleLogin()
  }
}

// 添加键盘监听
onMounted(() => {
  window.addEventListener('keyup', onKeyUp)
})

// 移除键盘监听
onBeforeUnmount(() => {
  window.removeEventListener('keyup', onKeyUp)
})
</script>
<template>
  <el-row class="login-container">
    <!-- 左侧：公司信息（仅在大屏幕显示） -->
    <el-col :span="16" :xs="0" :sm="0" :md="16" class="left">
      <div>
        <div>电子设计管理平台</div>
        <div>深圳市鼎骁科技有限公司</div>
      </div>
    </el-col>
    <!-- 右侧：登录表单（自适应屏幕大小） -->
    <el-col :span="8" :xs="24" :sm="24" :md="8" class="right">
      <h2 class="title">欢迎登录</h2>
      <div>
        <span class="line"></span>
        <span>账号密码登录</span>
        <span class="line"></span>
      </div>
      <el-form ref="ruleFormRef" :rules="rules" :model="loginData">
        <el-form-item prop="username">
          <el-input v-model="loginData.username" placeholder="请输入用户名">
            <template #prefix>
              <el-icon>
                <component :is="icons.User" />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            type="password"
            v-model="loginData.password"
            placeholder="请输入密码"
            show-password
          >
            <template #prefix>
              <el-icon>
                <component :is="icons.Lock" />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-button
            class="login-button"
            round
            color="#0a12b1"
            @click="handleLogin"
            :loading="loading"
          >
            登 录
          </el-button>
        </el-form-item>
      </el-form>
    </el-col>
  </el-row>
</template>

<style scoped lang="scss">
.login-container {
  @apply min-h-screen bg-indigo-500;
}

.login-container .left,
.login-container .right {
  @apply flex items-center justify-center;
}

.login-container .right {
  @apply bg-white flex-col;
}

.left > div > div:first-child {
  @apply font-bold text-5xl text-white mb-4;
  @media (max-width: 768px) {
    @apply text-3xl;
  }
}

.left > div > div:last-child {
  @apply text-gray-200 text-2xl;
  @media (max-width: 768px) {
    @apply text-xl;
  }
}

.right .title {
  @apply font-bold text-3xl text-gray-800;
  @media (max-width: 768px) {
    @apply text-2xl;
  }
}

.right > div {
  @apply flex items-center justify-center my-5 text-gray-300 space-x-2;
}

.right .line {
  @apply h-[1px] w-16 bg-gray-300;
  @media (max-width: 768px) {
    @apply w-10;
  }
}

.right > el-form {
  @apply w-[250px];
  @media (max-width: 768px) {
    @apply w-[85vw] max-w-[300px];
  }
}

.login-button {
  @apply w-[250px] bg-indigo-500;
  @media (max-width: 768px) {
    @apply w-[85vw] max-w-[300px];
  }
}

/* 手机尺寸专用样式 */
@media (max-width: 768px) {
  .login-container {
    @apply flex items-center justify-center;
  }

  .login-container .right {
    @apply min-h-screen w-full justify-center;
    padding: 20px;
    box-sizing: border-box;
  }

  .right > div {
    @apply my-4;
  }
}

/* 平板尺寸样式 */
@media (min-width: 769px) and (max-width: 1024px) {
  .left > div > div:first-child {
    @apply text-4xl;
  }

  .left > div > div:last-child {
    @apply text-xl;
  }

  .right .title {
    @apply text-2xl;
  }

  .right > el-form {
    @apply w-[220px];
  }

  .login-button {
    @apply w-[220px];
  }
}
</style>
